import type { JSONContent } from '@tiptap/core';

export type BlogStatus = 'rascunho' | 'publicado';

export const BLOG_CATEGORY_OPTIONS = [
  { id: 'pais', nome: 'Para Pais' },
  { id: 'educadores', nome: 'Para Educadores' },
  { id: 'profissionais', nome: 'Para Profissionais' },
  { id: 'inclusao', nome: 'Inclusão Digital' },
  { id: 'educacao', nome: 'Educação Especial' },
  { id: 'recursos', nome: 'Novos Recursos' },
  { id: 'atualizacoes', nome: 'Atualizações' },
] as const;

export type BlogCategory = (typeof BLOG_CATEGORY_OPTIONS)[number]['id'];

/**
 * Cor do bloco sólido no card da listagem (Blog.tsx) — paleta curta e fixa
 * (não um seletor de cor livre) pra garantir contraste bom com o texto
 * branco em cima em qualquer opção. `className` é a classe Tailwind de
 * fundo já pronta, usada tanto no card quanto no seletor do editor.
 */
export const BLOG_CARD_COLOR_OPTIONS = [
  { id: 'azul', nome: 'Azul', className: 'bg-primary' },
  { id: 'coral', nome: 'Coral', className: 'bg-warm' },
  { id: 'verde', nome: 'Verde', className: 'bg-emerald-700' },
  { id: 'roxo', nome: 'Roxo', className: 'bg-violet-600' },
  { id: 'rosa', nome: 'Rosa', className: 'bg-rose-700' },
] as const;

export type BlogCardColor = (typeof BLOG_CARD_COLOR_OPTIONS)[number]['id'];

/**
 * `posts/{slug}` no Firestore do blog (projeto separado) — 1 doc por artigo,
 * doc ID = slug (evita query pra resolver slug -> doc). `blocos` é o JSON
 * nativo do editor Tiptap, não o array plano do exemplo original da spec —
 * decisão confirmada com o usuário (guarda toda a formatação inline sem
 * precisar reinventar um parser próprio).
 */
export interface BlogPost {
  /** = slug (doc ID). */
  id: string;
  titulo: string;
  slug: string;
  resumo: string;
  /** Data URL JPEG em base64 (sem Firebase Storage — plano Spark não deixa
   *  habilitar Storage sem virar Blaze); '' quando ainda não tem capa. Cada
   *  imagem (capa + inline nos `blocos`) é comprimida bem apertado no cliente
   *  pra o doc inteiro caber no limite de ~1MiB do Firestore. */
  capa: string;
  /** Documento Tiptap (`editor.getJSON()`) — nós de imagem guardam a imagem
   *  como data URL base64 direto no `src` (`allowBase64: true`, ver
   *  `editor/blockEditorExtensions.ts`), não uma URL externa. */
  blocos: JSONContent;
  autorId: string;
  autorNome: string;
  /** Data URL JPEG comprimida (~30KB) do avatar do autor — denormalizada do
   *  perfil de autor (`authors/{uid}`) no momento da CRIAÇÃO do post, igual
   *  `autorNome`; trocar a foto depois não reescreve posts antigos. */
  autorFotoUrl?: string;
  categoria?: BlogCategory;
  /** Cor do bloco do card na listagem — ausente em posts antigos, cai pro
   *  azul padrão (ver BLOG_CARD_COLOR_OPTIONS e Blog.tsx). */
  corCard?: BlogCardColor;
  status: BlogStatus;
  criadoEm: number;
  atualizadoEm: number;
  publicadoEm?: number;
}

/** Payload de criação — servidor gera slug/id, define status inicial e timestamps. */
export interface CreateBlogPostInput {
  titulo: string;
  resumo: string;
  capa: string;
  blocos: JSONContent;
  categoria?: BlogCategory;
  corCard?: BlogCardColor;
}

/** Payload de atualização (salvar rascunho) — nunca muda `status`/`publicadoEm`. */
export type UpdateBlogPostInput = Partial<CreateBlogPostInput>;
